import React, { useContext, useEffect, useRef, useState } from "react";
import AppContext from "../../context/app.context";
import { IAppContext } from "../../interfaces/context.interfaces";
import {
  IEvent,
  IEventImage,
  IStationPlanetImage,
} from "../../interfaces/map.interfaces";
import { showDate } from "../../utils/event.utils";
import { hasPlanetImagery, isHanProject } from "../../utils/station.utils";
import styles from "./StationImagesGallery.module.css";

type GallerySource = "station" | "planet";

const MAX_CLOUD_PCT = 50;
const MIN_CLEAR_PCT = 50;

interface IGalleryItem {
  key: string;
  name: string;
  date: string;
  footer?: string;
}

interface IGalleryMonthGroup {
  key: string;
  year: string;
  monthLabel: string;
  items: IGalleryItem[];
}

const isClearPlanetImage = (image: IStationPlanetImage): boolean =>
  !!image.quality &&
  image.quality.cloudPct <= MAX_CLOUD_PCT &&
  image.quality.clearPct >= MIN_CLEAR_PCT;

const getDateTime = (date: string): number => {
  const time = new Date((date || "").split("/").join("-")).getTime();
  return isNaN(time) ? 0 : time;
};

const sortNewestFirst = (items: IGalleryItem[]): IGalleryItem[] =>
  [...items].sort((a, b) => getDateTime(b.date) - getDateTime(a.date));

// expects items sorted newest first; groups keep that order
const groupByMonth = (
  items: IGalleryItem[],
  language?: string
): IGalleryMonthGroup[] => {
  const groups: IGalleryMonthGroup[] = [];
  items.forEach((item) => {
    const match = /^(\d{4})-(\d{2})/.exec(showDate(item.date) || "");
    const key = match ? `${match[1]}-${match[2]}` : "";
    let group = groups.find((g) => g.key === key);
    if (!group) {
      group = {
        key,
        year: match ? match[1] : "",
        monthLabel: match
          ? new Date(
              Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)
            ).toLocaleString(language === "de" ? "de-DE" : "en-US", {
              month: "long",
              timeZone: "UTC",
            })
          : language === "de"
          ? "Unbekanntes Datum"
          : "Unknown date",
        items: [],
      };
      groups.push(group);
    }
    group.items.push(item);
  });
  return groups;
};

function StationImagesGallery({
  eventObject,
  closeGallery,
  language,
}: {
  eventObject: IEvent | null;
  closeGallery: () => void;
  language?: string;
}) {
  const appContext: IAppContext = useContext(AppContext);
  const [token, setToken] = useState("");
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | undefined>(
    appContext?.projects?.id
  );
  const previousStationImages = useRef<{ eventId?: string; keys: string[] } | null>(null);
  const [source, setSource] = useState<GallerySource>("station");
  const [planetImages, setPlanetImages] = useState<IStationPlanetImage[] | null>(
    null
  );
  const [planetLoading, setPlanetLoading] = useState(false);
  const [planetError, setPlanetError] = useState(false);
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  useEffect(() => {
    appContext?.services?.firebaseService?.auth?.currentUser
      ?.getIdToken()
      .then((value) => setToken(value));
  }, [appContext?.services?.firebaseService?.auth?.currentUser]);

  useEffect(() => {
    if (projectId) {
      return;
    }
    let cancelled = false;
    appContext?.services?.projectService
      ?.getMyData()
      .then((data: any) => {
        if (!cancelled) {
          setProjectId(data?.data?.[0]?.id);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [projectId, appContext?.services?.projectService]);

  const eventId = eventObject?.properties.id;
  const showPlanetToggle =
    isHanProject(projectId) && hasPlanetImagery(eventObject?.properties);
  const hasStationImages = (eventObject?.properties.images || []).length > 0;

  useEffect(() => {
    const keys = (eventObject?.properties.images || []).map(image => image.key);
    const previous = previousStationImages.current;
    const newImage = previous?.eventId === eventId && keys.some(key => !previous.keys.includes(key));
    if (newImage) {
      setSource("station");
      setPreviewKey(null);
      setSelectedYear(null);
      setSelectedMonth(null);
    } else if (showPlanetToggle && !hasStationImages) {
      setSource("planet");
    }
    previousStationImages.current = { eventId, keys };
  }, [eventId, eventObject, showPlanetToggle, hasStationImages]);

  useEffect(() => {
    if (source !== "planet" || !eventId || planetImages !== null) {
      return;
    }
    let cancelled = false;
    setPlanetLoading(true);
    setPlanetError(false);
    appContext.services.imageService
      .getStationPlanetImages(eventId)
      .then((images) => {
        if (!cancelled) {
          setPlanetImages(images);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPlanetError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPlanetLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [source, eventId, planetImages, appContext.services.imageService]);

  if (!eventObject || !eventId) {
    return null;
  }

  const isPlanet = showPlanetToggle && source === "planet";
  const images: IEventImage[] = eventObject.properties.images || [];

  const items: IGalleryItem[] = sortNewestFirst(
    isPlanet
      ? (planetImages || []).filter(isClearPlanetImage).map((image) => ({
          key: image.key,
          name: image.sceneId,
          date: image.date,
          footer: image.quality
            ? `PlanetScope · ${Math.round(image.quality.clearPct)}% ${
                language === "de" ? "klar" : "clear"
              }`
            : "PlanetScope",
        }))
      : images.map((image) => ({
          key: image.key,
          name: image.name,
          date: image.date,
          footer: image.author,
        }))
  );
  const monthGroups = groupByMonth(items, language);
  const years = monthGroups
    .map((group) => group.year)
    .filter((year, index, all) => all.indexOf(year) === index);
  const activeYear =
    selectedYear !== null && years.includes(selectedYear)
      ? selectedYear
      : years[0];
  const monthsOfYear = monthGroups.filter((group) => group.year === activeYear);
  const activeGroup =
    monthsOfYear.find((group) => group.key === selectedMonth) ||
    monthsOfYear[0];

  const selectYear = (year: string) => {
    setSelectedYear(year);
    setSelectedMonth(null);
  };

  const getImageUrl = (key: string) => {
    if (!token) {
      return "";
    }
    return isPlanet
      ? appContext.services.imageService.getStationPlanetImageUrl(
          eventId,
          key,
          token
        )
      : appContext.services.imageService.getRetrieveUrl(eventId, key, token);
  };

  const switchSource = (next: GallerySource) => {
    setPreviewKey(null);
    setSelectedYear(null);
    setSelectedMonth(null);
    setSource(next);
  };

  const renderContent = () => {
    if (isPlanet && planetLoading) {
      return (
        <div className={styles.emptyState}>
          {language === "de"
            ? "Aufnahmen werden geladen..."
            : "Loading Planet images..."}
        </div>
      );
    }
    if (isPlanet && planetError) {
      return (
        <div className={styles.emptyState}>
          {language === "de"
            ? "Aufnahmen konnten nicht geladen werden."
            : "Planet images could not be loaded."}
        </div>
      );
    }
    if (items.length === 0) {
      return (
        <div className={styles.emptyState}>
          {isPlanet
            ? language === "de"
              ? "Keine wolkenfreien Aufnahmen (3m) vorhanden."
              : "No cloud-free Planet images for this station."
            : language === "de"
            ? "Keine Auffälligkeiten vorhanden."
            : "No images for this station."}
        </div>
      );
    }
    return (
      <>
        <div className={styles.filterBar}>
          <label className={styles.filterField}>
            <span className={styles.filterLabel}>
              {language === "de" ? "Jahr" : "Year"}
            </span>
            <select
              className={styles.filterSelect}
              value={activeYear}
              onChange={(e) => selectYear(e.target.value)}
            >
              {years.map((year) => (
                <option key={year || "unknown"} value={year}>
                  {year || (language === "de" ? "Unbekannt" : "Unknown")}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.filterField}>
            <span className={styles.filterLabel}>
              {language === "de" ? "Monat" : "Month"}
            </span>
            <select
              className={styles.filterSelect}
              value={activeGroup?.key}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {monthsOfYear.map((group) => (
                <option key={group.key || "unknown"} value={group.key}>
                  {group.monthLabel} ({group.items.length})
                </option>
              ))}
            </select>
          </label>
        </div>
        {activeGroup ? renderGrid(activeGroup.items) : null}
      </>
    );
  };

  const renderGrid = (gridItems: IGalleryItem[]) => {
    return (
      <div className={styles.grid}>
        {gridItems.map((item) => (
          <article key={item.key} className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.fileIcon}>IMG</div>
              <span className={styles.fileName} title={item.name}>
                {item.name}
              </span>
            </div>
            <div
              className={styles.cardBody}
              onClick={() => setPreviewKey(item.key)}
            >
              {token ? (
                <img
                  className={styles.thumbnail}
                  src={getImageUrl(item.key)}
                  alt={item.name}
                  loading="lazy"
                />
              ) : null}
            </div>
            <div className={styles.cardFooter}>
              <div>{showDate(item.date)}</div>
              {item.footer ? <div>{item.footer}</div> : null}
            </div>
          </article>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.galleryOverlay}>
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>
              {language === "de" ? "Auffälligkeiten" : "Station Images"}
            </h2>
            <p className={styles.subtitle}>
              {isPlanet && planetImages === null ? "-" : items.length}{" "}
              {language === "de" ? "Bilder" : "images"}
            </p>
          </div>
          <div className={styles.headerActions}>
            {showPlanetToggle ? (
              <div className={styles.pillToggle} role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={!isPlanet}
                  className={`${styles.pill} ${
                    !isPlanet ? styles.pillActive : ""
                  }`}
                  onClick={() => switchSource("station")}
                >
                  {language === "de" ? "Auffälligkeiten" : "Station"}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={isPlanet}
                  className={`${styles.pill} ${
                    isPlanet ? styles.pillActive : ""
                  }`}
                  onClick={() => switchSource("planet")}
                >
                  {language === "de" ? "Alle Aufnahmen (3m)" : "Planet"}
                </button>
              </div>
            ) : null}
            <button
              type="button"
              className={styles.closeButton}
              onClick={closeGallery}
              aria-label={language === "de" ? "Schließen" : "Close"}
            >
              ×
            </button>
          </div>
        </div>

        <div className={styles.content}>{renderContent()}</div>
      </div>

      {previewKey ? (
        <div
          className={styles.previewOverlay}
          onClick={() => setPreviewKey(null)}
        >
          <button
            type="button"
            className={styles.previewClose}
            onClick={() => setPreviewKey(null)}
            aria-label={language === "de" ? "Schließen" : "Close"}
          >
            ×
          </button>
          <img
            className={styles.previewImage}
            src={getImageUrl(previewKey)}
            alt=""
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ) : null}
    </div>
  );
}

export default StationImagesGallery;
